import React, { useEffect, useState, useMemo, useRef } from "react";
import { motion, animate, useInView } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Parallax } from "react-parallax";
import { FaUsers, FaBook, FaChalkboardTeacher, FaAward } from "react-icons/fa";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import SEO from "../components/SEO";
import StructuredData from "../components/StructuredData";
import { useSettings } from "../hooks/useSettings";

// Animation variants
const fadeIn = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.6 },
  },
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.2,
    },
  },
};

const StatCard = ({ icon, count, label, delay }) => {
  const [displayValue, setDisplayValue] = useState("0");
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.4 });

  // Extract numeric target and suffix (e.g., "+")
  const targetValue = useMemo(() => {
    const numeric = parseInt(String(count).replace(/[^\d]/g, ""), 10);
    return Number.isNaN(numeric) ? 0 : numeric;
  }, [count]);
  const suffix = useMemo(() => {
    const match = String(count).match(/(\D+)$/);
    return match ? match[1].trim() : "";
  }, [count]);

  useEffect(() => {
    if (!isInView) return;
    const controls = animate(0, targetValue, {
      duration: 1.2,
      onUpdate: (latest) => {
        const formatted = new Intl.NumberFormat().format(Math.round(latest));
        setDisplayValue(`${formatted}${suffix ? ` ${suffix}` : ""}`.trim());
      },
    });
    return () => controls.stop();
  }, [isInView, targetValue, suffix]);

  return (
    <motion.div
      ref={ref}
      className="bg-white rounded-lg shadow-lg p-6 text-center"
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay }}
      whileHover={{
        y: -10,
        boxShadow:
          "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
      }}
    >
      <div className="text-safir-primary text-4xl mb-4 flex justify-center">
        {icon}
      </div>
      <h3 className="text-3xl font-bold text-safir-dark mb-2">{displayValue}</h3>
      <p className="text-gray-600">{label}</p>
    </motion.div>
  );
};

const TeamMember = ({ name, role, image, index }) => {
  return (
    <motion.div
      className="text-center"
      variants={fadeIn}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount: 0.3 }}
      transition={{ delay: index * 0.1 }}
    >
      <div className="relative mx-auto w-48 h-48 mb-4 overflow-hidden rounded-full">
        <img
          src={image}
          alt={name}
          className="w-full h-full object-cover transition-transform duration-500 hover:scale-110"
        />
      </div>
      <h3 className="text-xl font-bold text-safir-dark">{name}</h3>
      <p className="text-safir-primary">{role}</p>
    </motion.div>
  );
};

const AboutUsPage = () => {
  const { theme } = useSettings();
  const { t } = useTranslation();
  const [teamMembers, setTeamMembers] = useState([]);
  const [loadingTeam, setLoadingTeam] = useState(true);
  
  useEffect(() => {
    // Scroll to top when page loads
    window.scrollTo(0, 0);
  }, []);

  // Fetch team members from database
  useEffect(() => {
    const fetchTeamMembers = async () => {
      try {
        const response = await fetch("/api/leadership");
        if (response.ok) {
          const data = await response.json();
          setTeamMembers(data || []);
        }
      } catch (error) {
        console.error("Error fetching team members:", error);
      } finally {
        setLoadingTeam(false);
      }
    };

    fetchTeamMembers();
  }, []);

  return (
      <div className="min-h-screen bg-gray-50">
          <SEO 
            title="About Us - Our Mission & Vision"
            description="Learn about Safir Elite's commitment to educational excellence. Discover our mission, values, leadership team, and 10+ years of academic success in Afghanistan."
            canonical="/about"
            ogType="website"
            keywords="about us, Safir Elite, educational center, Afghanistan, mission, vision, leadership team"
          />
          <StructuredData type="EducationalOrganization" />
          <Navbar />

          {/* Hero Section */}
          <Parallax
              blur={{ min: -15, max: 15 }}
              bgImage={theme.coverImages?.about}
              bgImageAlt="University campus"
              strength={200}
          >
              <div className="py-40 px-4 bg-black bg-opacity-50">
                  <div className="container mx-auto text-center text-white">
                      <motion.h1
                          className="text-4xl md:text-5xl font-bold mb-4"
                          initial={{ opacity: 0, y: -20 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ duration: 0.5 }}
                      >
                          {t('about.title')}
                      </motion.h1>
                      <motion.div
                          className="w-24 h-1 bg-safir-primary mx-auto mb-6"
                          initial={{ width: 0 }}
                          animate={{ width: 96 }}
                          transition={{ duration: 0.8 }}
                      ></motion.div>
                      <motion.p
                          className="text-xl max-w-2xl mx-auto"
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          transition={{ duration: 0.5, delay: 0.3 }}
                      >
                          {t('about.subtitle')}
                      </motion.p>
                  </div>
              </div>
          </Parallax>

          {/* Our Story Section */}
          <section className="py-16 px-4">
              <div className="container mx-auto">
                  <div className="max-w-4xl mx-auto">
                      <motion.h2
                          className="text-3xl font-bold text-center text-safir-dark mb-8"
                          initial={{ opacity: 0 }}
                          whileInView={{ opacity: 1 }}
                          viewport={{ once: true }}
                      >
                          {t('about.ourStory')}
                      </motion.h2>

                      <motion.div
                          className="space-y-6 text-gray-700"
                          initial={{ opacity: 0, y: 20 }}
                          whileInView={{ opacity: 1, y: 0 }}
                          viewport={{ once: true }}
                          transition={{ duration: 0.6 }}
                      >
                          <p>
                              {t('about.storyPart1')}
                          </p>
                          <p>
                              {t('about.storyPart2')}
                          </p>
                          <p>
                              {t('about.storyPart3')}
                          </p>
                      </motion.div>
                  </div>
              </div>
          </section>

          {/* Mission & Vision Section */}
          <section className="py-16 px-4 bg-safir-dark text-white">
              <div className="container mx-auto">
                  <div className="grid md:grid-cols-2 gap-12">
                      <motion.div
                          className="bg-safir-primary bg-opacity-20 p-8 rounded-lg"
                          initial={{ opacity: 0, x: -50 }}
                          whileInView={{ opacity: 1, x: 0 }}
                          viewport={{ once: true }}
                          transition={{ duration: 0.6 }}
                      >
                          <h3 className="text-2xl font-bold mb-4">
                              {t('about.ourMission')}
                          </h3>
                          <p className="mb-4">
                              {t('about.missionPart1')}
                          </p>
                          <p>
                              {t('about.missionPart2')}
                          </p>
                      </motion.div>

                      <motion.div
                          className="bg-safir-primary bg-opacity-20 p-8 rounded-lg"
                          initial={{ opacity: 0, x: 50 }}
                          whileInView={{ opacity: 1, x: 0 }}
                          viewport={{ once: true }}
                          transition={{ duration: 0.6 }}
                      >
                          <h3 className="text-2xl font-bold mb-4">
                              {t('about.ourVision')}
                          </h3>
                          <p className="mb-4">
                              {t('about.visionPart1')}
                          </p>
                          <p>
                              {t('about.visionPart2')}
                          </p>
                      </motion.div>
                  </div>
              </div>
          </section>

          {/* Stats Section */}
          <section className="py-16 px-4">
              <div className="container mx-auto">
                  <motion.h2
                      className="text-3xl font-bold text-center text-safir-dark mb-12"
                      initial={{ opacity: 0 }}
                      whileInView={{ opacity: 1 }}
                      viewport={{ once: true }}
                  >
                      {t('about.safirInNumbers')}
                  </motion.h2>

                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
                      <StatCard
                          icon={<FaUsers />}
                          count={t('about.students')}
                          label={t('about.studentsLabel')}
                          delay={0.1}
                      />
                      <StatCard
                          icon={<FaChalkboardTeacher />}
                          count={t('about.teachers')}
                          label={t('about.teachersLabel')}
                          delay={0.2}
                      />
                      <StatCard
                          icon={<FaBook />}
                          count={t('about.courses')}
                          label={t('about.coursesLabel')}
                          delay={0.3}
                      />
                      <StatCard
                          icon={<FaAward />}
                          count={t('about.yearsOfExcellence')}
                          label={t('about.yearsOfExcellenceLabel')}
                          delay={0.4}
                      />
                  </div>
              </div>
          </section>

          {/* Values Section */}
          <section className="py-16 px-4 bg-gray-100">
              <div className="container mx-auto">
                  <motion.h2
                      className="text-3xl font-bold text-center text-safir-dark mb-12"
                      initial={{ opacity: 0 }}
                      whileInView={{ opacity: 1 }}
                      viewport={{ once: true }}
                  >
                      {t('about.coreValues')}
                  </motion.h2>

                  <motion.div
                      className="grid md:grid-cols-2 lg:grid-cols-3 gap-8"
                      variants={staggerContainer}
                      initial="hidden"
                      whileInView="visible"
                      viewport={{ once: true, amount: 0.3 }}
                  >
                      <motion.div
                          className="bg-white p-6 rounded-lg shadow-md"
                          variants={fadeIn}
                      >
                          <h3 className="text-xl font-bold text-safir-dark mb-3">
                              {t('about.excellence')}
                          </h3>
                          <p className="text-gray-700">
                              {t('about.excellenceDesc')}
                          </p>
                      </motion.div>

                      <motion.div
                          className="bg-white p-6 rounded-lg shadow-md"
                          variants={fadeIn}
                      >
                          <h3 className="text-xl font-bold text-safir-dark mb-3">
                              {t('about.integrity')}
                          </h3>
                          <p className="text-gray-700">
                              {t('about.integrityDesc')}
                          </p>
                      </motion.div>

                      <motion.div
                          className="bg-white p-6 rounded-lg shadow-md"
                          variants={fadeIn}
                      >
                          <h3 className="text-xl font-bold text-safir-dark mb-3">
                              {t('about.inclusivity')}
                          </h3>
                          <p className="text-gray-700">
                              {t('about.inclusivityDesc')}
                          </p>
                      </motion.div>

                      <motion.div
                          className="bg-white p-6 rounded-lg shadow-md"
                          variants={fadeIn}
                      >
                          <h3 className="text-xl font-bold text-safir-dark mb-3">
                              {t('about.innovation')}
                          </h3>
                          <p className="text-gray-700">
                              {t('about.innovationDesc')}
                          </p>
                      </motion.div>

                      <motion.div
                          className="bg-white p-6 rounded-lg shadow-md"
                          variants={fadeIn}
                      >
                          <h3 className="text-xl font-bold text-safir-dark mb-3">
                              {t('about.community')}
                          </h3>
                          <p className="text-gray-700">
                              {t('about.communityDesc')}
                          </p>
                      </motion.div>

                      <motion.div
                          className="bg-white p-6 rounded-lg shadow-md"
                          variants={fadeIn}
                      >
                          <h3 className="text-xl font-bold text-safir-dark mb-3">
                              {t('about.lifelongLearning')}
                          </h3>
                          <p className="text-gray-700">
                              {t('about.lifelongLearningDesc')}
                          </p>
                      </motion.div>
                  </motion.div>
              </div>
          </section>

          {/* Leadership Team Section */}
          <section className="py-16 px-4">
              <div className="container mx-auto">
                  <motion.h2
                      className="text-3xl font-bold text-center text-safir-dark mb-12"
                      initial={{ opacity: 0 }}
                      whileInView={{ opacity: 1 }}
                      viewport={{ once: true }}
                  >
                      {t('about.leadershipTeam')}
                  </motion.h2>

                  {loadingTeam ? (
                      <div className="flex justify-center items-center py-12">
                          <div className="text-center">
                              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-safir-primary mx-auto mb-4"></div>
                              <p className="text-gray-600">Loading team members...</p>
                          </div>
                      </div>
                  ) : teamMembers.length > 0 ? (
                      <motion.div
                          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8"
                          variants={staggerContainer}
                          initial="hidden"
                          whileInView="visible"
                          viewport={{ once: true, amount: 0.3 }}
                      >
                          {teamMembers.map((member, index) => (
                              <TeamMember
                                  key={member.id}
                                  name={member.name}
                                  role={member.position}
                                  image={member.image}
                                  index={index}
                              />
                          ))}
                      </motion.div>
                  ) : (
                      <div className="text-center py-12">
                          <p className="text-gray-600 text-lg">
                              No team members available at the moment.
                          </p>
                      </div>
                  )}
              </div>
          </section>

          {/* CTA Section */}
          <section className="py-16 px-4 bg-safir-primary text-white">
              <div className="container mx-auto text-center">
                  <motion.h2
                      className="text-3xl font-bold mb-6"
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                  >
                      {t('about.joinCommunity')}
                  </motion.h2>
                  <motion.p
                      className="text-xl max-w-2xl mx-auto mb-8"
                      initial={{ opacity: 0 }}
                      whileInView={{ opacity: 1 }}
                      viewport={{ once: true }}
                      transition={{ delay: 0.2 }}
                  >
                      {t('about.joinDescription')}
                  </motion.p>
                  <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: 0.4 }}
          >
            <a href="/contact">
              <motion.button
                          
                          className="bg-white text-safir-primary font-bold py-3 px-8 rounded-full hover:bg-gray-100 transition-colors duration-300"
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                      >
                          {t('about.contactUsToday')}
                      </motion.button>
                      </a>
                      
                  </motion.div>
              </div>
          </section>

          <Footer />
      </div>
  );
};

export default AboutUsPage;
